import { useState } from 'react';
import { ArrowForward, RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  AgreementFieldPolicy,
  AgreementPackageOption,
  AgreementPrepareRequest,
} from '../../types';

import { AgreementsSetup } from './AgreementsSetup';

function initialFields(
  option: AgreementPackageOption,
): Record<string, AgreementFieldPolicy['initial_value']> {
  return Object.fromEntries(option.fields.map(field => [field.key, field.initial_value]));
}

/** Backend preparation is the continue action within Your services, not a separate UI phase. */
export function AgreementPreparationForm({
  packages,
  configRevision,
  onPrepare,
  pending,
}: {
  packages: AgreementPackageOption[];
  configRevision: string;
  onPrepare: (request: Omit<AgreementPrepareRequest, 'request_id'>) => void;
  pending: boolean;
}) {
  const [packageKey, setPackageKey] = useState(packages[0]?.package_key ?? '');
  const option = packages.find(item => item.package_key === packageKey);
  const [selectedServices, setSelectedServices] = useState<string[]>(
    option?.allowed_service_sets[0] ?? [],
  );
  const [fields, setFields] = useState(() => (option ? initialFields(option) : {}));
  // Use the server's canonical combination/order, never an invented combination.
  const permittedServices = option?.allowed_service_sets.find(
    selection =>
      selection.length === selectedServices.length &&
      selection.every(
        key =>
          selectedServices.includes(key) && option.services.some(service => service.key === key),
      ),
  );
  const validFields = option?.fields.every(field => {
    const value = fields[field.key];
    return (
      value &&
      value.type === field.initial_value.type &&
      (!field.allowed_values?.length ||
        field.allowed_values.some(
          choice => choice.type === value.type && choice.value === value.value,
        ))
    );
  });
  const valid = Boolean(option && permittedServices && validFields);
  const servicesContent = (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Which services are you signing up for?
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Your selection fills in the order form and determines the agreements you review. You can
          change it before signing.
        </Typography>
      </Box>
      <Box
        role="group"
        aria-label="Billing model"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}
      >
        {packages.map(item => {
          const selected = item.package_key === packageKey;
          return (
            <Card
              key={item.package_key}
              variant="outlined"
              sx={{
                borderWidth: 2,
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 2,
              }}
            >
              <CardActionArea
                disabled={pending}
                aria-pressed={selected}
                onClick={() => {
                  setPackageKey(item.package_key);
                  setSelectedServices(item.allowed_service_sets[0] ?? []);
                  setFields(initialFields(item));
                }}
                sx={{ p: 3, height: '100%' }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  {selected ? (
                    <RadioButtonChecked color="primary" />
                  ) : (
                    <RadioButtonUnchecked color="disabled" />
                  )}
                </Stack>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {item.package_kind === 'self_serve' ? 'Self-serve billing' : 'Managed services'}
                </Typography>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      {option ? (
        <>
          <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
            <Typography component="legend" variant="overline" color="text.secondary" sx={{ mb: 1 }}>
              Selected services
            </Typography>
            <Stack spacing={2}>
              {option.services.map(service => {
                const selected = selectedServices.includes(service.key);
                return (
                  <Paper
                    key={service.key}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderWidth: 2,
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%' }}
                      control={
                        <Checkbox
                          checked={selected}
                          disabled={pending}
                          onChange={(_, checked) => {
                            setSelectedServices(previous =>
                              checked
                                ? [...previous, service.key]
                                : previous.filter(key => key !== service.key),
                            );
                          }}
                        />
                      }
                      label={<Typography sx={{ fontWeight: 700 }}>{service.label}</Typography>}
                    />
                  </Paper>
                );
              })}
            </Stack>
          </Box>
          {!permittedServices && (
            <Alert severity="info">Choose a supported service combination to continue.</Alert>
          )}
          {option.fields.length > 0 && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Order form details
                  </Typography>
                  <Typography color="text.secondary">
                    These details will appear in the agreements you review next.
                  </Typography>
                </Box>
                {option.fields.map(field => {
                  const value = fields[field.key] ?? field.initial_value;
                  const choices = field.allowed_values ?? [];
                  const choiceIndex = choices.findIndex(
                    choice => choice.type === value.type && choice.value === value.value,
                  );
                  const change = (raw: string) => {
                    if (choices.length) {
                      const next = choices[Number(raw)];
                      if (next) setFields(previous => ({ ...previous, [field.key]: next }));
                    } else if (value.type === 'string' || value.type === 'decimal')
                      setFields(previous => ({
                        ...previous,
                        [field.key]: { ...value, value: raw },
                      }));
                    else if (value.type === 'boolean')
                      setFields(previous => ({
                        ...previous,
                        [field.key]: { ...value, value: raw === 'true' },
                      }));
                  };
                  return (
                    <TextField
                      key={field.key}
                      label={field.label}
                      select={choices.length > 0 || value.type === 'boolean'}
                      value={
                        choices.length
                          ? choiceIndex < 0
                            ? ''
                            : choiceIndex
                          : value.value === null
                            ? ''
                            : String(value.value)
                      }
                      disabled={pending || !field.editable || value.type === 'null'}
                      onChange={event => change(event.target.value)}
                      helperText={
                        !field.editable
                          ? 'Prepared by JotPsych'
                          : choiceIndex < 0 && choices.length
                            ? 'Select one of the available values.'
                            : undefined
                      }
                    >
                      {choices.length
                        ? choices.map((choice, index) => (
                            <MenuItem key={index} value={index}>
                              {choice.value === null ? 'Not specified' : String(choice.value)}
                            </MenuItem>
                          ))
                        : value.type === 'boolean'
                          ? [
                              <MenuItem key="yes" value="true">
                                Yes
                              </MenuItem>,
                              <MenuItem key="no" value="false">
                                No
                              </MenuItem>,
                            ]
                          : undefined}
                    </TextField>
                  );
                })}
              </Stack>
            </Paper>
          )}
          <Box>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={pending || !valid}
              onClick={() => {
                if (pending || !valid || !permittedServices) return;
                onPrepare({
                  config_revision: configRevision,
                  package_key: packageKey,
                  selected_service_keys: permittedServices,
                  fields,
                });
              }}
            >
              {pending ? 'Opening agreements…' : 'Continue to agreements'}
            </Button>
          </Box>
        </>
      ) : (
        <Alert severity="info">Your JotPsych team has not configured a service package yet.</Alert>
      )}
    </Stack>
  );
  return (
    <AgreementsSetup
      preparationKey={configRevision}
      practiceName="Your practice"
      packages={[]}
      selectedPackageId=""
      canSign
      showStageRail={false}
      servicesContent={servicesContent}
      servicesSummary={option?.title}
      servicesPending={pending}
      onSelectPackage={() => undefined}
      onSign={() => Promise.resolve()}
      onContinueToIntake={() => undefined}
    />
  );
}
